// stores/settings.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'

const SUPPORTED = ['de', 'en', 'pl'] as const
type Lang = (typeof SUPPORTED)[number]

export type Theme = 'light' | 'dark' | 'auto'

const DEFAULT_LANG: Lang = 'de'
const DEFAULT_THEME: Theme = 'auto'

function normLang(lang: string | null | undefined): Lang {
  if (!lang) return DEFAULT_LANG
  const short = String(lang).toLowerCase().split(/[-_]/)[0] ?? DEFAULT_LANG
  return SUPPORTED.includes(short as Lang) ? (short as Lang) : DEFAULT_LANG
}

// Liefert das aktuell gültige visuelle Theme ('light' | 'dark') — berücksichtigt auch 'auto'.
function resolveVisualTheme(): 'light' | 'dark' {
  const stored = localStorage.getItem('theme')
  if (stored === 'light' || stored === 'dark') return stored

  // Auto → System-Präferenz
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  }
  return 'dark'
}

// Wende das visuelle Theme als CSS-Klasse auf <body> an.
function applyVisualTheme(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return
  const body = document.body
  if (!body) return
  body.classList.remove('theme-dark', 'theme-light')
  body.classList.add(`theme-${theme}`)
}

// Initialisiere den Store-Wert aus dem Local Storage (für Persistenz beim Neuladen)
const initialLang = normLang(localStorage.getItem('lang'))

export const useSettingsStore = defineStore('settings', () => {
  const currentLang = ref(initialLang)
  const currentTheme = ref<Theme>(DEFAULT_THEME)

  // Explizites Theme sofort auf die Seite anwenden (bei 'auto' → System-Präferenz)
  applyVisualTheme(resolveVisualTheme())

  function setLang(newLang: string) {
    const normalized = normLang(newLang)
    currentLang.value = normalized

    try {
      localStorage.setItem('lang', normalized)
    } catch {}
  }

  function setTheme(theme: Theme): void {
    if (theme === 'light' || theme === 'dark' || theme === 'auto') {
      currentTheme.value = theme
      try {
        localStorage.setItem('theme', theme)
      } catch {}
      applyVisualTheme(resolveVisualTheme())
    }
  }

  // Cyclic toggling: auto → light → dark → auto
  function toggleTheme(): void {
    const next: Record<Theme, Theme> = {
      auto: 'light',
      light: 'dark',
      dark: 'auto',
    }
    setTheme(next[currentTheme.value])
  }

  return { currentLang, currentTheme, setLang, setTheme, toggleTheme }
})
